import React, { useState, useEffect } from 'react';
import { Table, Button, Tag, Popconfirm, Space, Input, Modal, Form, Typography, Avatar, Tooltip, Badge, Select } from 'antd';
import { DeleteOutlined, TeamOutlined, EditOutlined, UserAddOutlined, UserDeleteOutlined, MessageOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import api from '../api';

const { Title, Text } = Typography;

function GroupManagePage() {
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [detailModal, setDetailModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [msgModal, setMsgModal] = useState(false);
  const [form] = Form.useForm();

  const loadGroups = async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 20 };
      if (keyword) params.keyword = keyword;
      const { list, total: t } = await api.get('/admin/groups', { params });
      setGroups(list);
      setTotal(t);
      setPage(p);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadGroups(); }, []);

  const handleSearch = () => loadGroups(1);

  const handleCreate = async () => {
    const values = form.getFieldsValue();
    if (!values.name) return;
    await api.post('/admin/groups', values);
    setCreateModal(false);
    form.resetFields();
    loadGroups(page);
  };

  const handleDelete = async (id) => {
    await api.delete(`/admin/groups/${id}`);
    loadGroups(page);
  };

  const handleViewDetail = async (group) => {
    setSelectedGroup(group);
    setDetailModal(true);
    const { group: detail, members: m } = await api.get(`/admin/groups/${group.id}`);
    setMembers(m || []);
  };

  const handleViewMessages = async (group) => {
    setSelectedGroup(group);
    setMsgModal(true);
    const { messages: msgs } = await api.get(`/admin/groups/${group.id}/messages`, { params: { page: 1, limit: 50 } });
    setMessages(msgs || []);
  };

  const handleRemoveMember = async (groupId, userId) => {
    await api.delete(`/admin/groups/${groupId}/members/${userId}`);
    const { members: m } = await api.get(`/admin/groups/${groupId}`);
    setMembers(m || []);
  };

  const roleMap = { owner: { label: '群主', color: 'gold' }, admin: { label: '管理员', color: 'blue' }, member: { label: '成员', color: 'default' } };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '群名称', width: 180,
      render: (_, r) => (
        <Space>
          {r.avatar ? <Avatar size="small" src={r.avatar} /> : <Avatar size="small" icon={<TeamOutlined />} style={{ background: '#07C160' }} />}
          <Text strong>{r.name}</Text>
        </Space>
      ),
    },
    { title: '群主', dataIndex: 'owner_name', width: 120 },
    { title: '成员数', dataIndex: 'member_count', width: 80, align: 'center' },
    { title: '群公告', dataIndex: 'announcement', ellipsis: true, render: (v) => v || <Text type="secondary">未设置</Text> },
    {
      title: '状态', width: 80,
      render: (_, r) => r.status === 1 ? <Tag color="green">正常</Tag> : <Tag color="red">已解散</Tag>,
    },
    { title: '创建时间', dataIndex: 'created_at', width: 160, render: (v) => v ? new Date(v).toLocaleString() : '-' },
    {
      title: '操作', width: 220,
      render: (_, r) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button size="small" icon={<TeamOutlined />} onClick={() => handleViewDetail(r)} />
          </Tooltip>
          <Tooltip title="查看消息">
            <Button size="small" icon={<MessageOutlined />} onClick={() => handleViewMessages(r)} />
          </Tooltip>
          <Popconfirm title="确定解散该群组?" onConfirm={() => handleDelete(r.id)} icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4}>群组管理</Title>
        <Space>
          <Input placeholder="搜索群名称" value={keyword} onChange={(e) => setKeyword(e.target.value)} onPressEnter={handleSearch} style={{ width: 200 }} />
          <Button type="primary" onClick={handleSearch}>搜索</Button>
          <Button type="primary" icon={<TeamOutlined />} onClick={() => setCreateModal(true)} style={{ background: '#07C160' }}>创建群组</Button>
        </Space>
      </div>
      <Table columns={columns} dataSource={groups} rowKey="id" loading={loading}
        pagination={{ current: page, total, pageSize: 20, onChange: loadGroups, showSizeChanger: false }} size="small" />

      {/* 创建群组 */}
      <Modal title="创建群组" open={createModal} onOk={handleCreate} onCancel={() => setCreateModal(false)}>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="群名称" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="avatar" label="群头像URL"><Input /></Form.Item>
          <Form.Item name="ownerId" label="群主ID" rules={[{ required: true }]}><Input type="number" /></Form.Item>
          <Form.Item name="announcement" label="群公告"><Input.TextArea rows={3} /></Form.Item>
          <Form.Item name="memberIds" label="初始成员ID列表"><Input placeholder="多个ID用逗号分隔" /></Form.Item>
        </Form>
      </Modal>

      {/* 群组详情 */}
      <Modal title={`群组详情 - ${selectedGroup?.name || ''}`} open={detailModal} footer={null} onCancel={() => setDetailModal(false)} width={600}>
        {selectedGroup && (
          <>
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary">群公告：</Text>
              <div style={{ marginTop: 8, padding: 12, background: '#f5f5f5', borderRadius: 6 }}>
                {selectedGroup.announcement || '暂无公告'}
              </div>
            </div>
            <Text strong>群成员 ({members.length})</Text>
            <Table dataSource={members} rowKey="id" size="small" pagination={{ pageSize: 10 }} style={{ marginTop: 8 }}
              columns={[
                { title: '用户', width: 150, render: (_, r) => <Space><Avatar size="small" src={r.avatar} />{r.nickname}</Space> },
                { title: '群内角色', width: 100, render: (_, r) => <Tag color={roleMap[r.role]?.color}>{roleMap[r.role]?.label}</Tag> },
                { title: '入群时间', width: 160, dataIndex: 'created_at', render: (v) => v ? new Date(v).toLocaleString() : '-' },
                {
                  title: '操作', width: 80,
                  render: (_, r) => r.role !== 'owner' ? (
                    <Popconfirm title="移除该成员?" onConfirm={() => handleRemoveMember(selectedGroup.id, r.id)}>
                      <Button size="small" danger icon={<UserDeleteOutlined />}>移除</Button>
                    </Popconfirm>
                  ) : null,
                },
              ]}
            />
          </>
        )}
      </Modal>

      {/* 群消息 */}
      <Modal title={`群消息 - ${selectedGroup?.name || ''}`} open={msgModal} footer={null} onCancel={() => setMsgModal(false)} width={600}>
        <Table dataSource={messages} rowKey="id" size="small" pagination={{ pageSize: 20 }}
          columns={[
            { title: '发送者', width: 120, dataIndex: 'sender_name' },
            { title: '内容', dataIndex: 'content', ellipsis: true },
            { title: '类型', width: 80, dataIndex: 'type', render: (v) => <Tag>{v}</Tag> },
            { title: '时间', width: 160, dataIndex: 'created_at', render: (v) => v ? new Date(v).toLocaleString() : '-' },
          ]}
        />
      </Modal>
    </>
  );
}

export default GroupManagePage;
